import React from 'react';

import { formatPrice } from '@shared/helpers';
import { Icon, Input, Skeleton, UiButton } from '@shared/ui';

import cls from './EquipmentHeader.module.css';
import { useEquipmentHeader } from '../model/useEquipmentHeader';

interface EquipmentHeaderProps {
    filterValue: string;
    isInitialLoading?: boolean;
    onFilterChange: (value: string) => void;
}

export const EquipmentHeader = ({
    filterValue,
    isInitialLoading = false,
    onFilterChange,
}: EquipmentHeaderProps) => {
    const {
        t,
        totalInfo,
        isSearchMode,
        exportEquipmentUrl,
        openSearchMode,
        closeSearchMode,
        getPositionText,
    } = useEquipmentHeader({ onFilterChange });

    return (
        <div className={cls.EquipmentHeader}>
            <h2 className={cls.EquipmentHeaderTitle}>{t('BoardHeader.return.title')}</h2>
            {!isSearchMode && (
                <div className={cls.EquipmentHeaderStatisticsWrapper}>
                    <UiButton
                        onClick={openSearchMode}
                        variant="icon"
                        className={cls.searchButton}
                        startIcon={
                            <Icon width={14} height={14} iconName="search" fill="currentColor" />
                        }
                    />
                    <a href={exportEquipmentUrl} target="_blank" rel="noreferrer">
                        <UiButton
                            variant="icon"
                            className={cls.downloadButton}
                            startIcon={
                                <Icon
                                    width={14}
                                    height={14}
                                    iconName="downloadDocument"
                                    fill="currentColor"
                                />
                            }
                        />
                    </a>

                    {isInitialLoading && (
                        <div className={cls.statisticsSkeleton}>
                            <Skeleton width="100%" height={28} border="3px" />
                        </div>
                    )}
                    {!isInitialLoading && totalInfo && (
                        <p className={cls.EquipmentHeaderStatistics}>
                            <span data-id="inventory-count">
                                {getPositionText(totalInfo.inventoryCount)}
                            </span>
                            <span> {t('BoardHeader.return.for_the_amount')} </span>
                            <span data-id="total-inventory-cost">
                                {formatPrice(totalInfo.totalInventoryCost, {
                                    maximumFractionDigits: 2,
                                    currencySign: totalInfo.currency.sign,
                                })}
                            </span>
                            <span className={cls.EquipmentHeaderSeparator} />
                            <span>{t('BoardHeader.return.written_off')} </span>
                            <span data-id="decommissioned-inventory-count">
                                {getPositionText(totalInfo.decommissionedInventoryCount)}
                            </span>
                            <span> {t('BoardHeader.return.for_the_amount')} </span>
                            <span data-id="decommissioned-inventory-cost">
                                {formatPrice(totalInfo.decommissionedInventoryCost, {
                                    maximumFractionDigits: 2,
                                    currencySign: totalInfo.currency.sign,
                                })}
                            </span>
                        </p>
                    )}
                </div>
            )}
            {isSearchMode && (
                <Input
                    autoFocus
                    type="search"
                    value={filterValue}
                    onChange={(event) => onFilterChange(event.target.value)}
                    onFocus={(event) => event.target.select()}
                    rightIconHandler={closeSearchMode}
                    rightIcon={<Icon width={10} height={11} iconName="close" />}
                />
            )}
        </div>
    );
};
